import { FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { allSmoLanguages, SmoLanguage } from '../../../../../Models/Smo/SmoLanguage';
import { allWidgetDisplayMode, WidgetDisplayMode } from '../../../../../Models/Widget/WidgetDisplayMode';
import UserStoreActions from '../../../../../Modules/Redux/Global/User/UserStoreActions';
import StreamSelector from '../../../../../Modules/Redux/Pages/Stream/StreamSelector';
import { Optional } from '../../../../../Utils/Types';
import { BoxSection } from '../../../Components/BoxSection/BoxSection';
import { Button } from '../../../Components/Button/Button';
import { HiddenField } from '../../../Components/HiddenField/HiddenField';
import { MediaDeviceMenu } from '../../../Components/MediaDeviceMenu/MediaDeviceMenu';
import { Menu } from '../../../Components/Menu/Menu';
import { PageLayout } from '../../../Components/PageLayout/PageLayout';
import { ScrollView } from '../../../Components/ScrollView/ScrollView';
import { Slider } from '../../../Components/Slider/Slider';
import { SmoLanguageMenu } from '../Components/SmoLanguageMenu/SmoLanguageMenu';

export const StreamPageVideoWidget: FunctionComponent = () => {
  const dispatch = useDispatch();
  const widgetUrl: Optional<string> = useSelector(StreamSelector.widgetUrl);
  const widgetDisplayMode: Optional<WidgetDisplayMode> = useSelector(StreamSelector.widgetDisplayMode);
  const widgetMoonLanguage: Optional<SmoLanguage> = useSelector(StreamSelector.widgetMoonLanguage);
  const widgetScale: Optional<number> = useSelector(StreamSelector.widgetScale);

  function didChangeWidgetDisplayMode(displayMode: string): void {
    dispatch(UserStoreActions.updateCurrentUserWidgetSettings({ displayMode: displayMode as WidgetDisplayMode }));
  }

  function didChangeWidgetMoonLanguage(language: string): void {
    dispatch(UserStoreActions.updateCurrentUserWidgetSettings({ moonLanguage: language as SmoLanguage }));
  }

  function didChangeWidgetScale(scale: number): void {
    dispatch(UserStoreActions.updateCurrentUserWidgetSettings({ scale }));
  }

  function resetToDefault(): void {
    dispatch(UserStoreActions.resetCurrentUserWidgetSettings());
  }

  return (
    <ScrollView className="h-full py-6 bg-gray-900">
      <PageLayout>
        <div className="v-stack space-y-4" style={{ maxWidth: '400px' }}>
          <h2 className="large-title text-white">Stream</h2>

          <BoxSection title="Video input">
            <MediaDeviceMenu />
          </BoxSection>

          <BoxSection
            title="Game language"
            footer="Used for moon detection. Match the selection with your current game language."
          >
            <SmoLanguageMenu />
          </BoxSection>

          <div className="py-2 opacity-10">
            <hr />
          </div>

          <BoxSection
            title="Widget url"
            footer="Copy this url into your broadcasting software to add the widget on your stream."
          >
            {widgetUrl ? <HiddenField text={widgetUrl} /> : undefined}
          </BoxSection>

          <div className="py-2 opacity-10">
            <hr />
          </div>

          <BoxSection title="Display" footer="The infos displayed on the widget.">
            {widgetDisplayMode ? (
              <Menu
                items={allWidgetDisplayMode.map((displayMode) => ({ id: displayMode, item: displayMode }))}
                defaultValue={widgetDisplayMode}
                onChange={didChangeWidgetDisplayMode}
              />
            ) : undefined}
          </BoxSection>

          <BoxSection title="Moon language" footer="The language of the moons displayed on the widget.">
            {widgetDisplayMode ? (
              <Menu
                items={allSmoLanguages.map((language) => ({ id: language, item: language }))}
                defaultValue={widgetMoonLanguage}
                onChange={didChangeWidgetMoonLanguage}
              />
            ) : undefined}
          </BoxSection>

          <BoxSection title="Font size" footer="Adjust the font size of the widget to fit your stream look.">
            {widgetScale !== undefined ? (
              <Slider className="w-full" defaultValue={widgetScale} min={0} max={100} onChange={didChangeWidgetScale} />
            ) : undefined}
          </BoxSection>

          <div className="py-2 opacity-10">
            <hr />
          </div>

          <BoxSection footer="Reset the appearance of the widget to default.">
            <Button
              title="Reset to default"
              tooltip="Reset the appearance of the widget to default."
              onClick={resetToDefault}
            />
          </BoxSection>
        </div>
      </PageLayout>
    </ScrollView>
  );
};
